/**
 * A book card.
 *
 * Titles come from the API already resolved for the active language, so the card
 * never chooses between `title_en` and `title_ar` itself. Where a title is in a
 * different script from the interface, `bidi-isolate` stops it reordering the
 * surrounding text.
 */

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Badge, Card, cx } from '@/components/ui'
import type { BookListItem, BookStatus } from '@/types/api'

const STATUS_TONE: Record<BookStatus, 'success' | 'warning' | 'info' | 'neutral'> = {
  available: 'success',
  reserved: 'warning',
  on_loan: 'info',
  unavailable: 'neutral',
}

export const StatusBadge = ({ status }: { status: BookStatus }) => {
  const { t } = useTranslation()
  return <Badge tone={STATUS_TONE[status]}>{t(`book.status.${status}`)}</Badge>
}

const CoverPlaceholder = ({ title }: { title: string }) => (
  <div
    className="flex size-full items-center justify-center bg-gradient-to-br from-brand-soft to-accent-soft p-3"
    aria-hidden="true"
  >
    <span className="line-clamp-3-safe bidi-isolate text-center text-xs font-semibold text-brand-strong">
      {title}
    </span>
  </div>
)

export const BookCard = ({ book }: { book: BookListItem }) => {
  const { t } = useTranslation()

  return (
    <Card as="li" className="group overflow-hidden transition hover:shadow-[var(--shadow-raised)]">
      <Link to={`/books/${book.slug}`} className="flex h-full flex-col">
        <div className="aspect-3/4 w-full overflow-hidden bg-surface-sunken">
          {book.cover_image ? (
            <img
              src={book.cover_image}
              alt={t('book.coverOf', { title: book.title })}
              loading="lazy"
              className="size-full object-cover transition duration-300 group-hover:scale-[1.03]"
            />
          ) : (
            <CoverPlaceholder title={book.title} />
          )}
        </div>

        <div className="flex flex-1 flex-col gap-1.5 p-3">
          <h3 className="line-clamp-2-safe bidi-isolate text-sm font-semibold text-ink">
            {book.title}
          </h3>
          <p className="line-clamp-2-safe bidi-isolate text-xs text-ink-muted">
            {book.author.name || t('book.unknownAuthor')}
          </p>

          <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1.5">
            <StatusBadge status={book.status} />
            {book.publication_year && (
              <span className="text-xs text-ink-subtle force-ltr">{book.publication_year}</span>
            )}
          </div>
        </div>
      </Link>
    </Card>
  )
}

/** Compact row form, used in dense lists such as the admin queue. */
export const BookRow = ({ book, className }: { book: BookListItem; className?: string }) => {
  const { t } = useTranslation()
  return (
    <Link to={`/books/${book.slug}`} className={cx('flex min-w-0 items-center gap-3', className)}>
      <div className="size-12 shrink-0 overflow-hidden rounded-md bg-surface-sunken">
        {book.cover_image ? (
          <img
            src={book.cover_image}
            alt=""
            loading="lazy"
            className="size-full object-cover"
          />
        ) : (
          <CoverPlaceholder title={book.title} />
        )}
      </div>
      <div className="min-w-0">
        <p className="truncate bidi-isolate text-sm font-medium text-ink">{book.title}</p>
        <p className="truncate bidi-isolate text-xs text-ink-subtle">
          {book.author.name || t('book.unknownAuthor')}
        </p>
      </div>
    </Link>
  )
}

export const BookGrid = ({ books }: { books: BookListItem[] }) => (
  <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-5">
    {books.map((book) => (
      <BookCard key={book.id} book={book} />
    ))}
  </ul>
)
